import { ApiPromise } from "@polkadot/api";
import { web3FromSource } from "@polkadot/extension-dapp";
import { InjectedAccountWithMeta } from "@polkadot/extension-inject/types";
import { SubmittableExtrinsic } from "@polkadot/api/types";
import { ISubmittableResult } from "@polkadot/types/types";
import { Setter } from "@/types";
import { formatForExtrinsic } from "@/utils";

export type transactionStatus = {
  status:
    | "preparing"
    | "awaiting"
    | "broadcasting"
    | "inBlock"
    | "finalized"
    | "error";
  extrinsicHash?: string;
  errorMessage?: string;
  process?: "staking" | "unstaking";
} | null;

const handleTransaction = async (
  api: ApiPromise,
  account: InjectedAccountWithMeta,
  transaction: SubmittableExtrinsic<"promise", ISubmittableResult>,
  cb: (status: string, hex: string, txHash?: string) => void,
  setStatus: Setter<transactionStatus>,
  onFinalized?: () => void
) => {
  if (!account) {
    console.error("Transaction failed: No account provided.");
    return;
  }

  if (!(await api.isReady)) {
    console.error("Transaction failed: API is not ready.");
    return;
  }

  try {
    console.log("Fetching signer...");
    const injector = await web3FromSource(account.meta.source);

    console.log("Preparing transaction...");
    setStatus({ status: "preparing" });

    await transaction.signAndSend(
      account.address,
      { signer: injector.signer, nonce: -1 },
      ({ events = [], status, dispatchError, txHash }) => {
        console.log("Transaction status:", status.type);

        if (status.isReady) {
          setStatus({ status: "awaiting" });
          console.log("Transaction is ready to be signed...");
        }

        if (status.isBroadcast) {
          setStatus({ status: "broadcasting" });
          console.log("Transaction has been broadcasted...");
        }

        if (status.isInBlock) {
          const extrinsicHash = status.asInBlock.toHex();
          console.log(`Transaction included in block: ${extrinsicHash}`);
          cb("isInBlock", extrinsicHash);
          setStatus((prev) =>
            prev?.status === "error"
              ? { ...prev, extrinsicHash }
              : { status: "inBlock", extrinsicHash: txHash?.toString() }
          );
        }

        if (status.isFinalized) {
          const extrinsicHash = status.asFinalized.toHex();
          console.log(`Transaction finalized in block: ${extrinsicHash}`);
          cb("isFinalized", extrinsicHash, txHash?.toString());
          setStatus((prev) => {
            if (prev?.status === "error") {
              return { ...prev, extrinsicHash: txHash?.toString() };
            }
            if (!prev?.status) {
              return null;
            }
            onFinalized && onFinalized();
            return { status: "finalized", extrinsicHash: txHash?.toString() };
          });
        }

        if (dispatchError) {
          console.error(
            "Transaction failed with dispatch error:",
            dispatchError.toString()
          );

          if (dispatchError.isModule) {
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { docs, name, section } = decoded;
            console.error(`Error: ${section}.${name}: ${docs.join(" ")}`);
            const message = `${section}.${name}: ${docs.join(" ")}`;
            setStatus({
              status: "error",
              extrinsicHash: txHash?.toString(),
              errorMessage: message,
            });
            cb("Error", message);
          } else {
            const message = dispatchError.toString();
            setStatus({
              status: "error",
              extrinsicHash: txHash?.toString(),
              errorMessage: message,
            });
            cb("Error", message);
          }
        }

        // Extract on-chain errors from events
        events
          .filter(({ event }) => api.events.system.ExtrinsicFailed.is(event))
          .forEach(
            ({
              event: {
                data: [error],
              },
            }) => {
              console.error("Extrinsic failed:", error.toString());
              const message = error.toString();
              cb("Error", message);
            }
          );
      }
    );
  } catch (e) {
    console.error("Transaction error:", e);
    setStatus({
      status: "error",
      errorMessage: e?.toString() || "something went wrong",
    });
  }
};

export const bridgeTokens = async (
  api: ApiPromise,
  account: InjectedAccountWithMeta,
  data: { amount: string; chain: string; receipientAddress: string },
  cb: (status: string, hex: string, txHash?: string) => void,
  setStatus: Setter<transactionStatus>,
  onFinalized?: () => void
) => {
  if (account && (await api.isReady)) {
    const { amount, chain, receipientAddress } = data;
    const value = formatForExtrinsic(amount);
    const tx = api.tx.bridge.bridge(chain, receipientAddress, value);
    await handleTransaction(api, account, tx, cb, setStatus, onFinalized);
  }
};

export const getGasFee = async (
  api: ApiPromise,
  account: InjectedAccountWithMeta,
  data: { amount: string; chain: string; receipientAddress: string }
) => {
  if (account && (await api.isReady)) {
    try {
      const { amount, chain, receipientAddress } = data;
      const gasFee = (
        await api.tx.bridge
          .bridge(chain, receipientAddress, amount)
          .paymentInfo(account.address)
      ).partialFee;
      return gasFee;
    } catch (err) {
      console.error("Failed to get the gas fee");
    }
  }
};
